<template>
    <div class="competition-settings">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <div ref="messages">
                <panel-info-box v-if="successMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ successMessage }}</b>
                    </p>
                </panel-info-box>
                <panel-info-box v-if="errorMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ errorMessage }}</b>
                    </p>
                </panel-info-box>
            </div>

            <div class="lang-switcher mab16">
                <button
                    v-for="lang in langs"
                    :key="lang"
                    type="button"
                    class="lang-switcher__btn"
                    :class="{ 'lang-switcher__btn--active': activeLang === lang }"
                    @click="activeLang = lang"
                >
                    {{ lang.toUpperCase() }}
                </button>
            </div>

            <panel-competition-confirmation-form
                :key="activeLang"
                v-model="forms[activeLang]"
                :saving="saving"
                :sending-test="sendingTest"
                @save="saveSettings"
                @send-test="sendTest"
            />
        </template>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import PanelCompetitionConfirmationForm from '@/components/PanelCompetitionConfirmationForm';
    import PanelInfoBox from '@/components/PanelInfoBox';
    import { READ_PANEL_COMPETITION } from '@/store/actions.type';
    import {
        registerMailContentField,
        updateRegisterMailContent,
        sendTestRegister,
        htmlBreaksToNewlines,
        newlinesToHtmlBreaks,
    } from '@/api/confirmation';

    const LANGS = ['pl', 'en', 'de', 'cz', 'fr', 'lt', 'ua'];

    function emptyForms() {
        return LANGS.reduce((acc, lang) => {
            acc[lang] = '';
            return acc;
        }, {});
    }

    function asContent(value) {
        if (value == null) {
            return '';
        }
        const text = String(value);
        return text === 'null' || text === 'undefined' ? '' : text;
    }

    export default {
        name: 'PanelCompetitionSettingConfirmation',
        components: {
            PanelCompetitionConfirmationForm,
            PanelInfoBox,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                saving: false,
                sendingTest: false,
                langs: LANGS,
                activeLang: 'pl',
                forms: emptyForms(),
                successMessage: '',
                errorMessage: '',
            };
        },
        computed: {
            competitionId() {
                return this.competition.id;
            },
        },
        async created() {
            await this.loadAll();
            this.ready = true;
        },
        methods: {
            ...mapActions({
                readPanelCompetition: READ_PANEL_COMPETITION,
            }),
            scrollToMessages() {
                this.$nextTick(() => {
                    const el = this.$refs.messages;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        return;
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
            },
            normalizeError(e) {
                if (!e) {
                    return 'Wystąpił błąd';
                }
                if (typeof e === 'string') {
                    return e;
                }
                if (e.error) {
                    return e.error;
                }
                if (e.message) {
                    return e.message;
                }
                return 'Wystąpił błąd';
            },
            contentFromCompetition(lang) {
                if (!this.competition) {
                    return '';
                }
                return htmlBreaksToNewlines(asContent(this.competition[registerMailContentField(lang)]));
            },
            loadAll() {
                const next = emptyForms();
                LANGS.forEach((lang) => {
                    next[lang] = this.contentFromCompetition(lang);
                });
                this.forms = next;
            },
            async saveSettings() {
                const lang = this.activeLang;
                this.saving = true;
                this.successMessage = '';
                this.errorMessage = '';

                try {
                    await updateRegisterMailContent(
                        this.competitionId,
                        lang,
                        newlinesToHtmlBreaks(this.forms[lang])
                    );
                    await this.readPanelCompetition(this.competitionId);
                    this.$set(this.forms, lang, this.contentFromCompetition(lang));
                    this.successMessage = 'Treść e-maila została zapisana';
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                } finally {
                    this.saving = false;
                    this.scrollToMessages();
                }
            },
            async sendTest() {
                this.sendingTest = true;
                this.successMessage = '';
                this.errorMessage = '';

                try {
                    await sendTestRegister(
                        this.competitionId,
                        newlinesToHtmlBreaks(this.forms[this.activeLang])
                    );
                    this.successMessage = 'Wiadomość testowa została wysłana';
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                } finally {
                    this.sendingTest = false;
                    this.scrollToMessages();
                }
            },
        },
    };
</script>

<style scoped>
    .competition-settings {
        width: 100%;
    }
    .lang-switcher {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .lang-switcher__btn {
        min-width: 48px;
        padding: 8px 14px;
        border: 1px solid var(--color-blue-grey, #78909c);
        border-radius: 4px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-weight: 600;
    }
    .lang-switcher__btn--active {
        background: var(--color-dark-pastel-green, #66bb6a);
        border-color: var(--color-dark-pastel-green, #66bb6a);
        color: #fff;
    }
    @media (min-width: 992px) {
        .competition-settings {
            margin-left: 30px;
        }
    }
</style>
